const industrySolutionsData = {
  "water-monitoring": {
    title: "Water Monitoring",
    shortDescription: "Track water quality, usage patterns, and compliance in real time.",
    seoDescription: "Water monitoring solution for utilities and enterprises with remote meter reading, leak detection, flow analytics, and compliance-ready dashboards.",
    seoKeywords: "water monitoring, smart water metering, leak detection system, utility analytics, flow monitoring, water IoT solution",
    seoImage: "https://fractallogy.com/images/menu_icons/water_monitoring.png",
    icon: "/images/menu_icons/water.png",
    heroImage: "/images/menu_icons/water_monitoring.png",
    intro:
      "Modernize utility operations with connected metering, remote visibility, and proactive alerts across city networks, industrial plants, and distributed facilities.",
    highlights: [
      "Remote meter reading with frequent and accurate usage snapshots",
      "Leak and anomaly detection to reduce non-revenue water losses",
      "Continuous flow and quality insights for reliable service delivery"
    ],
    modules: [
      {
        name: "Connected Meter Operations",
        text: "Aggregate data from residential, commercial, and distribution assets into one operational control layer."
      },
      {
        name: "Alert and Incident Response",
        text: "Trigger event-driven workflows for leaks, abnormal consumption, and pressure deviations before they escalate."
      },
      {
        name: "Conservation and Compliance Intelligence",
        text: "Transform field data into regulatory-friendly reports, conservation KPIs, and planning-ready analytics."
      }
    ],
    diagramImage: "/images/menu_icons/water_monitoring.png",
    architectureTitle: "How The Smart Water Stack Works",
    architectureDescription:
      "Field sensors and ultrasonic meters continuously send readings to gateways and cloud services, enabling near real-time monitoring, leak identification, and usage optimization from a central dashboard.",
    architectureSteps: [
      "Smart endpoints capture consumption, flow, pressure, and quality indicators at source.",
      "LPWAN and cellular channels relay readings from difficult locations with low power overhead.",
      "Cloud analytics convert raw readings into alerts, patterns, and operational recommendations."
    ],
    communicationOptions: [
      "LoRaWAN for long-range, low-power deployments across wide utility zones",
      "NB-IoT and 4G fallback paths for resilient telemetry continuity",
      "Flexible frequency and network strategy for mixed urban and semi-rural footprints"
    ],
    impactCards: [
      {
        title: "Operational Efficiency",
        text: "Reduce manual meter reads and improve maintenance prioritization with reliable remote telemetry."
      },
      {
        title: "Loss Prevention",
        text: "Identify leakage and abnormal behavior early to prevent avoidable water loss and infrastructure damage."
      },
      {
        title: "Customer Transparency",
        text: "Support better billing clarity and conservation behavior through detailed usage visibility."
      }
    ]
  },
  "energy-metering": {
    title: "Energy Metering",
    shortDescription: "Measure and optimize power consumption across connected assets.",
    seoDescription: "Wireless energy metering platform with power quality analytics, alarm automation, remote monitoring, and demand optimization for industrial operations.",
    seoKeywords: "energy metering, wireless energy meter, power quality monitoring, demand analytics, industrial energy monitoring, IoT metering platform",
    seoImage: "https://fractallogy.com/images/menu_icons/energy_monitoring.png",
    icon: "/images/menu_icons/energy.png",
    heroImage: "/images/menu_icons/energy_monitoring.png",
    themeVariant: "energy-metering",
    intro:
      "Digitize power monitoring with wireless metering, remote diagnostics, and live analytics so teams can control cost, reliability, and electrical performance at scale.",
    highlights: [
      "Capture active and reactive energy with high-frequency remote reads",
      "Monitor voltage, current, power factor, demand, and harmonics from one view",
      "Automate alarms for over/under voltage, current, power, and phase-loss events"
    ],
    modules: [
      {
        name: "Wireless Metering Fabric",
        text: "Deploy distributed single-phase and three-phase meters across buildings and production lines without heavy wiring overhead."
      },
      {
        name: "Power Quality and Safety Layer",
        text: "Track harmonics, imbalance, residual current, and cable temperature indicators to improve power health and operational safety."
      },
      {
        name: "Billing and Efficiency Intelligence",
        text: "Convert raw measurements into actionable cost analytics, peak-demand controls, and organization-wide performance benchmarks."
      }
    ],
    diagramImage: "/images/menu_icons/energy_monitoring.png",
    architectureTitle: "Energy Monitoring Architecture",
    architectureDescription:
      "Smart meters stream measurement telemetry through flexible communications into cloud analytics, enabling remote O&M, event monitoring, and cost optimization across dispersed electrical assets.",
    architectureSteps: [
      "Field meters collect kWh, voltage, current, frequency, and quality indicators at circuit level.",
      "Data is transmitted through RS485, LoRa, LoRaWAN, 4G, NB-IoT, or Wi-Fi based on site topology.",
      "Dashboards and APIs surface anomalies, trends, and control actions for operations teams."
    ],
    communicationOptions: [
      "Multi-channel communications for centralized and widely distributed meter deployments",
      "Gateway-assisted or direct cloud uplink patterns depending on project constraints",
      "Open protocols and API-first design for enterprise platform integration"
    ],
    impactCards: [
      {
        title: "Remote Operations at Scale",
        text: "Reduce manual inspections with centralized visibility and faster diagnostics across multiple sites."
      },
      {
        title: "Power Reliability and Safety",
        text: "Detect abnormal electrical behavior early and support preventive maintenance workflows."
      },
      {
        title: "Cost and Demand Control",
        text: "Use interval-based analytics to reduce waste, optimize tariffs, and improve planning accuracy."
      }
    ],
    capabilityPillars: [
      "Remote Meter Reading",
      "Power Quality Analytics",
      "Alarm and Event Management",
      "Temperature and Leakage Monitoring"
    ]
  },
  "air-quality": {
    title: "Air Quality Monitoring",
    shortDescription: "Monitor indoor and outdoor air quality with actionable alerts.",
    seoDescription: "Air quality monitoring platform for smart cities and enterprises with real-time pollution alerts, analytics, and environmental intelligence.",
    seoKeywords: "air quality monitoring, PM2.5 monitoring, pollution detection, environmental IoT, smart city air analytics, air quality dashboard",
    seoImage: "https://fractallogy.com/images/menu_icons/air_quality.png",
    icon: "/images/menu_icons/air.png",
    heroImage: "/images/menu_icons/air_quality.png",
    themeVariant: "air-quality",
    intro:
      "Create an always-aware environmental intelligence layer using connected sensors, long-range telemetry, and proactive response workflows across cities, campuses, and industrial zones.",
    highlights: [
      "Continuously observe PM2.5, PM10, VOC, methane, CO2, and microclimate indicators",
      "Detect environmental anomalies early, including pollution spikes and risk patterns",
      "Support cleaner operations with data-backed conservation and compliance decisions"
    ],
    modules: [
      {
        name: "Distributed Sensor Fabric",
        text: "Deploy low-power sensor nodes across urban streets, plants, campuses, and remote terrain with centralized control."
      },
      {
        name: "Real-Time Detection and Alerts",
        text: "Automatically surface pollution events and threshold breaches to operations, safety, and public-response teams."
      },
      {
        name: "Environmental Analytics Layer",
        text: "Turn continuous readings into trend intelligence, heatmaps, and forecasting insights for long-term planning."
      }
    ],
    diagramImage: "/images/menu_icons/air_quality.png",
    architectureTitle: "Air Intelligence Architecture",
    architectureDescription:
      "Edge sensors stream high-frequency environmental readings through long-range networks into cloud analytics, where events are prioritized and actioned in near real time.",
    architectureSteps: [
      "Always-on field sensors capture pollution, gas, and atmospheric context at location level.",
      "Long-range low-power connectivity keeps data flowing reliably from dense and remote zones.",
      "Cloud analytics classifies incidents, predicts trends, and powers response dashboards."
    ],
    communicationOptions: [
      "LPWAN-ready deployments for broad coverage and low maintenance overhead",
      "Support for private, public, or hybrid network operating models",
      "Interoperable stack that coexists with Wi-Fi, Bluetooth, and cellular ecosystems"
    ],
    impactCards: [
      {
        title: "Public Health Visibility",
        text: "Provide stakeholders with timely, transparent insights on air quality behavior and risk windows."
      },
      {
        title: "Climate and Sustainability Action",
        text: "Use continuous environmental signals to guide reduction initiatives and track measurable outcomes."
      },
      {
        title: "Faster Incident Response",
        text: "Move from delayed reporting to event-led operations for pollution spikes and environmental hazards."
      }
    ],
    environmentPillars: [
      "Pollution Monitoring and Mitigation",
      "Flood, Hazard, and Disaster Signals",
      "Forest and Biodiversity Protection",
      "Smart City Environmental Benchmarking"
    ]
  },
  "steam-measurement": {
    title: "Steam Monitoring and Measurement",
    shortDescription: "Capture steam flow and thermal performance for process efficiency.",
    seoDescription: "Steam monitoring and measurement solution with compensated flow analytics, steam trap diagnostics, and thermal efficiency insights.",
    seoKeywords: "steam monitoring, steam flow measurement, steam trap monitoring, thermal efficiency, industrial steam analytics, process optimization",
    seoImage: "https://fractallogy.com/images/menu_icons/steam_monitoring.png",
    icon: "/images/menu_icons/steam.png",
    heroImage: "/images/menu_icons/steam_monitoring.png",
    themeVariant: "steam-measurement",
    intro:
      "Gain full visibility into steam performance with compensated flow measurement, trap health monitoring, and real-time efficiency analytics across your process network.",
    highlights: [
      "Measure mass and energy flow using pressure and temperature compensated telemetry",
      "Detect steam trap leakage and abnormal discharge before losses compound",
      "Track quality indicators, demand trends, and line-level efficiency in one control view"
    ],
    modules: [
      {
        name: "Compensated Flow Intelligence",
        text: "Combine flow, pressure, and temperature inputs to estimate true steam behavior for accurate reporting and control."
      },
      {
        name: "Steam Trap Performance Monitoring",
        text: "Use acoustic and thermal signatures to identify trap failures, leakage risks, and maintenance priorities."
      },
      {
        name: "Thermal Efficiency Analytics",
        text: "Correlate steam consumption with process output to reduce waste, optimize load distribution, and improve boiler-side utilization."
      }
    ],
    diagramImage: "/images/menu_icons/steam_monitoring.png",
    architectureTitle: "Steam System Monitoring Architecture",
    architectureDescription:
      "Field metering and trap sensors feed real-time data into edge or cloud analytics, enabling early anomaly detection, energy accountability, and predictive maintenance planning.",
    architectureSteps: [
      "Install flow sensors on key mains and branch lines with proper straight-run and dryness considerations.",
      "Collect pressure, temperature, and flow data continuously to compute mass and thermal energy movement.",
      "Overlay trap-condition events and alarm workflows to reduce losses and unplanned downtime."
    ],
    communicationOptions: [
      "Industrial telemetry over LoRaWAN, NB-IoT, or wired backbone where required",
      "Edge-based filtering for high-signal trap and leak events before cloud sync",
      "Protocol-friendly integration with supervisory, reporting, and maintenance platforms"
    ],
    impactCards: [
      {
        title: "Fuel and Utility Savings",
        text: "Detecting leaks and efficiency drift early helps lower energy waste and supports measurable annual savings."
      },
      {
        title: "Reliability and Uptime",
        text: "Move from periodic inspection to condition-based maintenance for critical steam assets."
      },
      {
        title: "Data-Backed Decision Making",
        text: "Provide operations, maintenance, and finance teams with shared KPIs for faster optimization."
      }
    ],
    capabilityPillars: [
      "Mass Flow and Thermal Energy Tracking",
      "Steam Trap Health Diagnostics",
      "Anomaly and Alarm Workflows",
      "Predictive Maintenance Readiness"
    ]
  },
  "waste-management": {
    title: "Waste Management",
    shortDescription: "Digitize waste tracking and improve sustainability reporting.",
    icon: "/images/menu_icons/waste.png",
    heroImage: "/images/main-slider/about-banner-bg.jpg",
    intro:
      "Manage waste streams with digital workflows, disposal traceability, and sustainability insights.",
    highlights: [
      "Track collection, movement, and disposal events",
      "Standardize handling workflows across facilities",
      "Generate ESG-friendly sustainability reports"
    ],
    modules: [
      {
        name: "Waste Lifecycle Tracking",
        text: "Capture every stage from generation to disposal with time-stamped and location-aware records."
      },
      {
        name: "Operations Workflows",
        text: "Create process templates for teams to ensure safe and consistent handling practices."
      },
      {
        name: "Sustainability Analytics",
        text: "Measure reduction outcomes and share dashboard-ready data with leadership and auditors."
      }
    ]
  },
  "optical-crm": {
    title: "Optical CRM",
    shortDescription: "Manage inventory, prescriptions, billing, and customer journeys.",
    icon: "/images/menu_icons/optical.png",
    heroImage: "/images/main-slider/pos_crm.png",
    intro:
      "Run your optical business with a unified CRM and POS platform built for modern eyewear operations.",
    highlights: [
      "Prescription and patient history management",
      "Inventory, billing, and GST-ready invoicing",
      "Customer lifecycle, reminders, and loyalty flows"
    ],
    modules: [
      {
        name: "Retail and Clinical Workflow",
        text: "Handle consultation notes, prescription tracking, and order fulfillment in one connected workflow."
      },
      {
        name: "Smart Inventory and Billing",
        text: "Manage frames, lenses, stock levels, and multi-store billing without operational friction."
      },
      {
        name: "Growth and Engagement Tools",
        text: "Drive repeat purchases with follow-up reminders, targeted offers, and customer insights."
      }
    ]
  }
};

export default industrySolutionsData;
